// THE LONG KINDLING — Room 18: "The Wood-path" (Act III) — the gated threshold; the rowan-line begins.
// Authored from research/wall/script/v3/rooms_17-21.md §Room 18 and research/wall/BUILD_PLAN.md.
//
// The first few yards in under the trees, BEHIND the line, where the cleared ground's working colour gives
// over to wet bark, leaf-rot, and a green-black light no fire reaches. The path is a root-knuckled deer-
// track, lined both sides with young ROWAN saplings set in a dead-straight seam — you walk between them or
// you don't walk. NO warm paint at all (no fire to ration; the wood is evenly, wrongly cold). The watchers
// are NOT here: you are past the backs now, behind the shield.
//
// Painting is 384x216; the room is 480x270, so ART coords scale by x1.25. No firemask (no fire in the wood).
//
// THE TWO INTERACTIONS (no soft-lock, no death):
//   - WALK THE SEAM (`stepped_off`): keep to the rowans (right). The tempting wrong way steps off "to take
//     the short way" — the path you make leads nowhere, you come out where you went in, a sapling left bent.
//     Reversible toggle; the sapling straightens by the next visit; knowledge is permanent.
//   - THE GATE (knowledge, not a lock): inward exits are `gatedBy round_decoded` (global). Until Rowan knows
//     what holds them out, he stops on his own at the seam's end. A knowledge-gate, never a soft-lock.
import { loadRoom, type Room } from "../../core/schema";

export const woodPath = {
  id: "wood_path",
  name: "The Wood-path",
  size: { w: 480, h: 270 },
  background: { color: 0x0b1410 },
  bgImage: "art/wood_path.png", // Retro-Diffusion painting; geometry aligned to it
  // hero starts at the mouth of the path, lowest/nearest, where the cleared ground gives over to wood
  spawn: { x: 240, y: 252 },
  actorRadius: 8,
  interactRadius: 64,
  // the one reversible try-the-wrong-way toggle (stepping off the seam). NOT a gate key — can't soft-lock.
  state: { stepped_off: false },
  // walkable floor = the deer-track path band up the centre of the painting (one convex band, foot of path)
  walkmesh: [
    [ { x: 150, y: 224 }, { x: 330, y: 224 }, { x: 360, y: 266 }, { x: 120, y: 266 } ],
  ],
  hotspots: [
    // ---- the rowan-line (the seam) — set in a line going IN; you walk between them or you don't walk
    { id: "rowan_line", verb: "examine", label: "the rowan-line", interactAt: { x: 200, y: 250 },
      line: "Rowans. Same as the one by our door, same as I'm named. Set in a line going in — not a hedge, not a fence — a seam. The town wards itself in small ways without remembering why; here the why's half-visible. You walk between them or you don't walk: the line is the safe passage, and a wall to everything either side.",
      polygon: [ { x: 96, y: 150 }, { x: 138, y: 150 }, { x: 138, y: 220 }, { x: 96, y: 220 } ] },

    // ---- the tied sprigs — old grey wool, the same the silent women shell beans in; the not-saying is the keeping
    { id: "tied_sprigs", verb: "examine", label: "the tied sprigs", interactAt: { x: 290, y: 250 },
      line: "Old grey wool, knotted on the saplings — the same wool the silent women shell beans in, the same grey as the cord on the latch-stakes. Somebody keeps them tied. Nobody'll say who, same as nobody'll say anything. The not-saying is the keeping.",
      polygon: [ { x: 342, y: 150 }, { x: 384, y: 150 }, { x: 384, y: 220 }, { x: 342, y: 220 } ] },

    // ---- the path ahead — it goes in further than it should; winning and getting lost are the same picture
    { id: "path_ahead", verb: "examine", label: "the path ahead", interactAt: { x: 240, y: 244 },
      line: "It goes in further than it should for ground this small. The further we push the felling, the more wood there is between the watch and what they hold off. Winning and getting lost are the same picture.",
      polygon: [ { x: 196, y: 70 }, { x: 284, y: 70 }, { x: 284, y: 140 }, { x: 196, y: 140 } ] },

    // ---- look back down the path — the town's just a coal of light; the scale of what the watch holds
    { id: "look_back", verb: "examine", label: "back down the path", interactAt: { x: 240, y: 254 },
      line: "From in here the town's just a coal of light at the bottom of the dark, and the watch a row of backs between it and me. All that standing, to hold a circle the size of a hearthstone against all this.",
      polygon: [ { x: 168, y: 240 }, { x: 312, y: 240 }, { x: 312, y: 264 }, { x: 168, y: 264 } ] },

    // ==== WALK THE SEAM — the rowan-line; the Chesterton beat in miniature (the crooked old way arrives) ====
    // false = kept to the rowans (right). true = stepped off "to take the short way" / cut a fresh straight
    // path (wrong: it leads nowhere, you come out where you went in, a sapling left bent). Reversible toggle:
    // the sapling straightens by the next visit; knowledge is permanent; no death, no soft-lock.
    { id: "walk_the_seam", verb: "use", label: "the short way off the seam", interactAt: { x: 290, y: 250 },
      line: "There's an itch to step off the rowans and cut a fresh straight path — take the short way in. Don't. Off the seam the wood isn't walkable ground; the path you make leads nowhere, and you come out exactly where you went in, having gained nothing, a young rowan you trod left bent. Keep to the rowans. The crooked old way is the only way that arrives.",
      effect: { toggle: ["stepped_off"] },
      polygon: [ { x: 330, y: 160 }, { x: 372, y: 160 }, { x: 372, y: 218 }, { x: 330, y: 218 } ] },

    // ---- pick up rowan-sprigs — warding; carried deeper as folk-comfort (R19-R21); no mechanical buff
    { id: "cut_sprigs", verb: "take", label: "rowan-sprigs", interactAt: { x: 200, y: 250 },
      line: "I cut a few sprigs from the line — hard berries, grey wool and all. Warding, the folk say; comfort, more like, to carry deeper in. No charm in it I could swear to. But I take them.",
      effect: { setGlobal: { rowan_sprigs: true } },
      polygon: [ { x: 104, y: 188 }, { x: 134, y: 188 }, { x: 134, y: 218 }, { x: 104, y: 218 } ] },
  ],
  // no fire in the wood: no warm fx at all — the evenness of the cold is the point. Cold motes only.
  fx: [
    { id: "wood_motes", kind: "motes", layer: "front", x: 160, y: 120, w: 160, h: 110, color: 0x6f8a86, count: 16, intensity: 0.7, seed: 1 },
  ],
  npcs: [
    // The watchers are NOT here — past the line, deeper than the thing they face. Their absence is the point.
  ],
  // Exits per GAME_MAP: warding-yard (R9), treeline/the Watch (R17), first-felling (R19), warding-stones (R22).
  // The INWARD exits (R19) gate on the round being decoded (Act III). Neighbours land later — kept commented
  // so the world boots clean. The gate pattern is shown on the (commented) first-felling exit.
  exits: [
    // back out to the treeline / the Watch (R17) and out to the warding-yard (R9) — the way home
    { id: "to_treeline", target: "the_edge_dusk", spawn: { x: 300, y: 248 }, transition: "fade",
      polygon: [ { x: 150, y: 244 }, { x: 198, y: 244 }, { x: 198, y: 264 }, { x: 150, y: 264 } ] },
    { id: "to_warding_yard", target: "warding_yard", spawn: { x: 240, y: 246 }, transition: "fade",
      polygon: [ { x: 282, y: 244 }, { x: 330, y: 244 }, { x: 330, y: 264 }, { x: 282, y: 264 } ] },
    // the rowan-seam side -> the warding-stones / rowan-line (R22)
    { id: "to_warding_stones", target: "warding_stones", spawn: { x: 240, y: 232 }, transition: "fade",
      polygon: [ { x: 150, y: 226 }, { x: 198, y: 226 }, { x: 198, y: 246 }, { x: 150, y: 246 } ] },
    // INWARD -> the first felling (R19) — gated on the decoded round (Act III; safe to go deeper)
    { id: "to_first_felling", target: "first_felling", spawn: { x: 280, y: 246 }, transition: "fade",
      gatedBy: { key: "round_decoded", scope: "global" },
      polygon: [ { x: 216, y: 226 }, { x: 264, y: 226 }, { x: 264, y: 246 }, { x: 216, y: 246 } ] },
  ],
};

export const room: Room = loadRoom(woodPath);
